import React from "react";
import { createRoot, Root } from "react-dom/client";
import "./styles/global.css";
import App from "./App";
import reportWebVitals from "./utils/reportWebVitals";
import Login from "./pages/LoginPage";
import { createLogger } from "./utils/logging";
import { getAuthType } from "./security/authStorage";
import {
  initializeEnvironment,
  initializeNavigation,
  setupNoAuthMode,
  setupClientPortalAutoLogin,
  initializeDefaultAuthType,
  handleOAuthCallback,
  getOAuthCallbackAuthType,
  getBootstrapState,
  isAuthEnabled,
} from "./utils/bootstrap";

const logger = createLogger("Bootstrap");

const CODE = "code";

// ============================================================================
// Application Bootstrap
// ============================================================================

async function bootstrap() {
  try {
    // Load environment and navigation configurations in parallel
    await Promise.all([initializeEnvironment(), initializeNavigation()]);

    // Setup no-auth mode for local development
    setupNoAuthMode();

    // Setup client portal auto-login
    setupClientPortalAutoLogin();

    // Get OAuth code from query params
    const query = new URLSearchParams(window.location.search);
    const code = query.get(CODE);

    // Initialize default auth type only if not processing OAuth callback
    if (!code) {
      initializeDefaultAuthType();
    }

    // Check if authentication is enabled
    if (!isAuthEnabled()) {
      renderApp("app");
      return;
    }

    // Handle OAuth callback
    if (code) {
      const callbackState = query.get('state');
      const authType = getOAuthCallbackAuthType(
        callbackState,
        getAuthType(),
      );
      await handleOAuthCallback(code, authType);
      // Remove the one-time authorization code and callback state.
      window.location.replace("/");
      return;
    }

    // Determine which component to render
    const state = getBootstrapState();
    renderApp(state);
  } catch (error) {
    logger.error("Initialization Failed", "Application bootstrap failed", {
      error,
    });
    // Fallback to login page on error
    renderApp("login");
  }
}

// ============================================================================
// React Root Rendering
// ============================================================================

// Singleton root — createRoot must only be called once per container.
let appRoot: Root | null = null;

function renderApp(state: "app" | "login") {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  let component;

  switch (state) {
    case "app":
      component = <App />;
      break;
    case "login":
    default:
      component = <Login />;
      break;
  }

  if (!appRoot) {
    appRoot = createRoot(rootElement);
  }
  appRoot.render(component);
}

// Start the application
bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
